import React, { useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  ModalFooter, Button, Input, Flex, Box, Text, Tag, TagLabel, TagCloseButton,
  Wrap, WrapItem, useToast, Spinner, Icon,
} from '@chakra-ui/react';
import { FiSend, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import axios from 'axios';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Send-test-email modal. Collects one or more recipient addresses and sends the
 * current email template (HTML + optional amp4email) as a test via
 * POST /email-template/send-test.
 */
export default function SendTestEmailModal({ isOpen, onClose, html, amp, subject }) {
  const toast = useToast();
  const [emails, setEmails] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  const addEmail = (raw) => {
    const val = String(raw || '').trim().replace(/,$/, '');
    if (!val) return;
    if (!EMAIL_RE.test(val)) { toast({ title: 'Invalid email', description: val, status: 'warning', duration: 2000 }); return; }
    if (emails.includes(val)) { setDraft(''); return; }
    setEmails(prev => [...prev, val]);
    setDraft('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') { e.preventDefault(); addEmail(draft); }
    else if (e.key === 'Backspace' && !draft && emails.length) { setEmails(prev => prev.slice(0, -1)); }
  };

  const removeEmail = (val) => setEmails(prev => prev.filter(x => x !== val));

  const handleClose = () => { setResults(null); onClose(); };

  const send = async () => {
    const all = draft.trim() ? [...emails, draft.trim()].filter((v, i, a) => a.indexOf(v) === i) : emails;
    const valid = all.filter(e => EMAIL_RE.test(e));
    if (draft.trim() && EMAIL_RE.test(draft.trim())) addEmail(draft);
    if (!valid.length) { toast({ title: 'Add at least one recipient email', status: 'warning', duration: 2000 }); return; }
    if (!html) { toast({ title: 'Nothing to send', description: 'Build the email first.', status: 'warning', duration: 2500 }); return; }
    setSending(true); setResults(null);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/email-template/send-test`,
        { to: valid, subject: subject || 'Test Email', html, amp: amp || undefined },
        { headers: {
          'x-access-token': localStorage.getItem('customer-portal'),
          'x-api-key': localStorage.getItem('api-key'),
        } });
      const rows = res.data?.results || [];
      setResults(rows);
      const okCount = rows.filter(r => r.ok).length;
      if (okCount) toast({ title: `Test sent to ${okCount} of ${rows.length}`, status: 'success', duration: 2500 });
      else toast({ title: 'Test email failed', description: res.data?.error || 'See details', status: 'error', duration: 3500 });
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      toast({ title: 'Send failed', description: msg, status: 'error', duration: 4000 });
      setResults(null);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="14px">
        <ModalHeader fontSize="17px">Send Test Email</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="12px" color="gray.500" mb={2}>
            Add one or more addresses (press Enter or comma between each). The current template is sent to each.
          </Text>
          <Box border="1px solid" borderColor="gray.200" borderRadius="8px" p="6px" _focusWithin={{ borderColor: 'var(--brand-primary)', boxShadow: '0 0 0 2px rgb(var(--brand-primary-rgb) / 0.145)' }}>
            <Wrap spacing={1.5} mb={emails.length ? 2 : 0}>
              {emails.map(e => (
                <WrapItem key={e}>
                  <Tag size="md" borderRadius="full" variant="subtle" colorScheme="blue">
                    <TagLabel fontSize="12px">{e}</TagLabel>
                    <TagCloseButton onClick={() => removeEmail(e)} />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
            <Input
              variant="unstyled" size="sm" px={1} value={draft} placeholder={emails.length ? 'Add another…' : 'name@example.com'}
              onChange={e => setDraft(e.target.value)} onKeyDown={onKeyDown} onBlur={() => draft.trim() && addEmail(draft)}
              sx={{ '&, &:focus, &:focus-visible': { border: 'none !important', boxShadow: 'none !important', outline: 'none !important' } }}
            />
          </Box>

          {results && (
            <Box mt={3} borderTop="1px solid" borderColor="gray.100" pt={2}>
              <Text fontSize="11px" fontWeight="700" color="gray.500" mb={1}>Results</Text>
              {results.map(r => (
                <Flex key={r.email} align="center" gap={2} fontSize="12px" py="2px">
                  <Icon as={r.ok ? FiCheckCircle : FiXCircle} color={r.ok ? 'green.500' : 'red.500'} />
                  <Text color="gray.700">{r.email}</Text>
                  {!r.ok && <Text color="red.400" noOfLines={1}>— {r.error}</Text>}
                </Flex>
              ))}
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={handleClose}>Close</Button>
          <Button size="sm" bg="var(--brand-primary)" colorScheme="blue" leftIcon={sending ? <Spinner size="xs" /> : <FiSend />}
            onClick={send} isDisabled={sending || (!emails.length && !draft.trim())}>
            {sending ? 'Sending…' : 'Send Test'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
